import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore, type IDropdownItem } from 'shared/ui'
import { InboxesApi } from 'entities/Inbox'
import {
  type IResponseInbox,
  type IResponseInboxConversationAssignment,
  type IParamsUpdateTeamByConversationAssignment,
  type IResponseInboxConversationManualAssignment,
  type IResponseInboxConversationHubspotAssignment,
  type IResponseHubspotAssignmentFields,
} from 'entities/Inbox/api/types'
import { type IResponseUser } from 'entities/Users/api/types'
import { IntegrationKey } from 'entities/Integrations'
import { EnumUserRoles } from 'entities/Users'
import { type InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'
import styles from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/styles.module.scss'
import { MemberLabel } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/ui/MemberLabel/MemberLabel'

export type AssignmentType = 'manual' | 'round_robin' | 'hubspot_assignment'

export type SentimentField =
  | 'smart_assignment_positive'
  | 'smart_assignment_neutral'
  | 'smart_assignment_negative'

const DEFAULT_HUBSPOT_PROPERTY = 'hubspot_owner_id'

export const defaultUnassignedDropdownItem: IDropdownItem = {
  id: 'unassigned',
  label: 'Unassigned',
  value: 'unassigned',
  data: null,
  avatarProps: {
    info: {
      icon: 'userLine',
      color: 'transparent',
    },
    size: 16,
  },
}

export const defaultRoundRobinDropdownItem: IDropdownItem = {
  id: 'round_robin',
  label: 'Round robin',
  value: 'round_robin',
  data: null,
  avatarProps: {
    info: {
      icon: 'repeatBold',
      color: 'transparent',
    },
    size: 16,
  },
}

export class ConversationAssignmentStore {
  private _loading = false
  private _tabSwitchingLoading = false
  private _conversationAssignment: IResponseInboxConversationAssignment | null = null
  private _inbox: IResponseInbox | null = null
  private _members: IResponseUser[] = []
  private _selectedMemberId = 0
  private _membersLoading = false
  private _searchValue = ''
  private _smartAssignment = false
  private _sentimentPositive: number | null = null
  private _sentimentNeutral: number | null = null
  private _sentimentNegative: number | null = null
  private _selectedRoundRobinMembers: IDropdownItem[] = []
  private _roundRobinMembersError: string | null = null
  private _roundRobinMembersOrderChanged = false
  private _selectedCRMFallbackMember: IDropdownItem | null = null
  private _crmFallbackType: 'unassigned' | 'round_robin' | null = null
  private _crmFallbackRoundRobinMembers: IDropdownItem[] = []
  private _crmFallbackRoundRobinSelected = false
  private _crmHubspotProperty: string | null = null
  private _hubspotAssignmentFields: IResponseHubspotAssignmentFields[] = []

  constructor(private _inboxSettingsStore: InboxSettingsStore) {
    makeAutoObservable(this)
  }

  init = async (inbox: IResponseInbox) => {
    this._inbox = inbox
    this.fetchConversationAssignment(inbox.id)
    this.loadMembers(inbox.id)
  }

  get conversationAssignment() {
    return this._conversationAssignment
  }

  get loading() {
    return this._loading
  }

  get tabSwitchingLoading() {
    return this._tabSwitchingLoading
  }

  get membersLoading() {
    return this._membersLoading
  }

  get searchValue() {
    return this._searchValue
  }

  get inbox() {
    return this._inbox
  }

  get hubspotIntegration() {
    return this._inboxSettingsStore.inboxLayerStore.entities.integrationsStore.getIntegration(
      IntegrationKey.hubspot
    )
  }

  get isHubspotIntegrationConnected() {
    return this.hubspotIntegration?.isConnected
  }

  get selectedAssignmentType() {
    if (!this._conversationAssignment) {
      return 'manual'
    }

    if (this._conversationAssignment.hubspot_assignment.enabled) {
      return 'hubspot_assignment'
    }

    if (this._conversationAssignment.round_robin.enabled) {
      return 'round_robin'
    }

    return 'manual'
  }

  get assignmentOptions() {
    return [
      {
        value: 'manual',
        title: 'Manual',
        description: 'Conversations are not automatically assigned to members of the inbox.',
        icon: 'userFilled',
        iconColor:
          this.selectedAssignmentType === 'manual'
            ? 'var(--content-brand-primary)'
            : 'var(--content-primary-tertiary)',
      },
      {
        value: 'round_robin',
        title: 'Round Robin',
        description: 'Conversations are assigned to inbox members in a sequential order.',
        icon: 'repeatBold',
        iconColor:
          this.selectedAssignmentType === 'round_robin'
            ? 'var(--content-brand-primary)'
            : 'var(--content-primary-tertiary)',
      },
      {
        value: 'hubspot_assignment',
        title: 'CRM',
        description: 'Conversations are assigned based on contact owners in your CRM.',
        icon: this.isHubspotIntegrationConnected ? 'hubspot' : 'hubspotDisabled',
        iconColor:
          this.selectedAssignmentType === 'hubspot_assignment'
            ? 'var(--content-brand-primary)'
            : 'var(--content-primary-tertiary)',
        disabled: !this.isHubspotIntegrationConnected,
      },
    ]
  }

  get selectedManualUserId() {
    return this._selectedMemberId
  }

  get selectedItem() {
    return this.filteredMembers.find((item) => item.id === this.selectedManualUserId)
  }

  getFilteredMembers = (
    memberMapping: (member: IResponseUser) => IDropdownItem,
    shouldAddUnassignedItem = true
  ) => {
    const unassignedItem: IDropdownItem = {
      id: 0,
      label: 'Unassigned',
      value: 'Unassigned',
      data: null,
      avatarProps: {
        info: {
          icon: 'userLine',
        },
        size: 16,
      },
    }

    const memberItems = this._members.map(memberMapping)
    const allItems = shouldAddUnassignedItem ? [unassignedItem, ...memberItems] : memberItems
    const searchLower = this._searchValue.toLowerCase().trim()

    return searchLower
      ? allItems.filter((item) => item.label.toLowerCase().includes(searchLower))
      : allItems
  }

  get filteredMembers() {
    return this.getFilteredMembers(this.memberToDropdownItem)
  }

  get filteredSmartAssignmentMembers() {
    return this.getFilteredMembers(this.memberToDropdownSmartAssignmentItem)
  }

  get filteredRoundRobinMembers() {
    return this.getFilteredMembers(this.allMembersAsDropdownItems, false)
  }

  get smartAssignment() {
    return this._smartAssignment
  }

  get selectedRoundRobinMembers() {
    return this._selectedRoundRobinMembers
  }

  get selectedRoundRobinMembersIds() {
    return this._selectedRoundRobinMembers.map((item) => item.id)
  }

  get roundRobinMembersError() {
    return this._roundRobinMembersError
  }

  get selectedCRMFallbackMember() {
    return this._selectedCRMFallbackMember
  }

  get crmHubspotProperty() {
    return this._crmHubspotProperty
  }

  get hubspotAssignmentFields() {
    return this._hubspotAssignmentFields
  }

  allMembersAsDropdownItems = (member: IResponseUser) => {
    return {
      ...this.defaultMemberToDropdownItem(member),
      noRenderEmptyActiveValue: true,
      labelContent: <MemberLabel member={member} />,
      className: styles.selectOptionWrapRoundRobin,
    }
  }

  fetchConversationAssignment = async (inboxId: number) => {
    try {
      runInAction(() => {
        this._loading = true
      })
      const { data } = await InboxesApi.getTeamByConversationAssignment(inboxId)
      this.setInitialValues(data)
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setInitialValues = (data: IResponseInboxConversationAssignment) => {
    this._conversationAssignment = data
    this._selectedMemberId = data.manual.default_user_id
    this._smartAssignment = data.manual.smart_assignment_enabled
    this._sentimentPositive = data.manual.smart_assignment_positive
    this._sentimentNeutral = data.manual.smart_assignment_neutral
    this._sentimentNegative = data.manual.smart_assignment_negative
    this._crmHubspotProperty = data.hubspot_assignment.integration_assignment_field_deleted
      ? null
      : data.hubspot_assignment.integration_assignment_field || DEFAULT_HUBSPOT_PROPERTY

    if (data.hubspot_assignment.enabled && !this.isHubspotIntegrationConnected) {
      this.updateAssignmentType('manual')
    }

    if (data.hubspot_assignment.enabled && this.isHubspotIntegrationConnected) {
      if (data.round_robin.enabled && data.round_robin.users.length > 0) {
        this._crmFallbackType = 'round_robin'
        if (this._members.length > 0) {
          this._crmFallbackRoundRobinMembers =
            this.createCRMFallbackRoundRobinMembersFromServerOrder(
              data.round_robin.users,
              this._members
            )
        } else {
          this._crmFallbackRoundRobinMembers = []
        }
        this._selectedCRMFallbackMember = defaultRoundRobinDropdownItem
      } else if (data.manual.enabled && data.manual.default_user_id > 0) {
        this._crmFallbackType = null
        const selectedMember = this._members.find((m) => m.id === data.manual.default_user_id)
        if (selectedMember) {
          this._selectedCRMFallbackMember = this.allMembersAsDropdownItems(selectedMember)
        }
      } else {
        this._crmFallbackType = 'unassigned'
        this._selectedCRMFallbackMember = defaultUnassignedDropdownItem
      }
    } else {
      this._crmFallbackType = null
      this._selectedCRMFallbackMember = null
      this._crmFallbackRoundRobinMembers = []
    }

    if (data.round_robin.enabled && data.round_robin.users.length > 0 && this._members.length > 0) {
      this._selectedRoundRobinMembers = this.createRoundRobinMembersFromServerOrder(
        data.round_robin.users,
        this._members
      )
      this._roundRobinMembersOrderChanged = false
    } else {
      this._selectedRoundRobinMembers = []
      this._roundRobinMembersOrderChanged = false
    }
  }

  private createRoundRobinMembersFromServerOrder = (
    users: { id: number }[],
    members: IResponseUser[],
    transformFn?: (member: IResponseUser) => IDropdownItem
  ) => {
    const membersMap = new Map(members.map((member) => [member.id, member]))

    const result: IDropdownItem[] = []
    for (const user of users) {
      const member = membersMap.get(user.id)
      if (member) {
        result.push(transformFn?.(member) ?? this.memberToDropdownItem(member))
      }
    }
    return result
  }

  private createCRMFallbackRoundRobinMembersFromServerOrder = (
    users: { id: number }[],
    members: IResponseUser[]
  ) => {
    return this.createRoundRobinMembersFromServerOrder(users, members, (member) =>
      this.allMembersAsDropdownItems(member)
    )
  }

  private getRoundRobinUserIds = (users: { id: number }[]) => {
    return users.map((user) => user.id)
  }

  private syncLocalValuesFromServer = (data: IResponseInboxConversationAssignment) => {
    this._selectedMemberId = data.manual.default_user_id

    if (!this._roundRobinMembersOrderChanged && this._members.length > 0) {
      if (data.round_robin.enabled && data.round_robin.users.length > 0) {
        this._selectedRoundRobinMembers = this.createRoundRobinMembersFromServerOrder(
          data.round_robin.users,
          this._members
        )
      } else {
        this._selectedRoundRobinMembers = []
      }
    }

    if (data.hubspot_assignment.enabled) {
      if (data.round_robin.enabled && data.round_robin.users.length > 0) {
        this._crmFallbackType = 'round_robin'

        if (!this._crmFallbackRoundRobinSelected) {
          this._crmFallbackRoundRobinMembers =
            this.createCRMFallbackRoundRobinMembersFromServerOrder(
              data.round_robin.users,
              this._members
            )
        }
        this._selectedCRMFallbackMember = defaultRoundRobinDropdownItem
        this._crmFallbackRoundRobinSelected = false
      } else if (data.manual.enabled && data.manual.default_user_id > 0) {
        this._crmFallbackType = null
        const selectedMember = this._members.find((m) => m.id === data.manual.default_user_id)
        if (selectedMember) {
          this._selectedCRMFallbackMember = this.allMembersAsDropdownItems(selectedMember)
        }
      } else {
        this._crmFallbackType = 'unassigned'
        this._selectedCRMFallbackMember = defaultUnassignedDropdownItem
      }
    } else {
      this._crmFallbackType = null
      this._selectedCRMFallbackMember = null
      this._crmFallbackRoundRobinMembers = []
    }
  }

  private updateConversationAssignment = async (
    updates: {
      manual?: Partial<IResponseInboxConversationManualAssignment>
      round_robin?: Partial<{ enabled: boolean; users: number[] }>
      hubspot_assignment?: Partial<IResponseInboxConversationHubspotAssignment>
    },
    rollbackCallback?: () => void
  ) => {
    const inboxId = this._inbox?.id
    if (!inboxId || !this._conversationAssignment) {
      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      const params: IParamsUpdateTeamByConversationAssignment = {
        manual: {
          ...this._conversationAssignment.manual,
          ...updates.manual,
        },
        round_robin: {
          enabled: updates.round_robin?.enabled ?? this._conversationAssignment.round_robin.enabled,
          users:
            updates.round_robin?.users ??
            this.getRoundRobinUserIds(this._conversationAssignment.round_robin.users),
        },
        hubspot_assignment: {
          ...this._conversationAssignment.hubspot_assignment,
          ...updates.hubspot_assignment,
        },
      }

      const { data } = await InboxesApi.updateTeamByConversationAssignment(inboxId, params)
      runInAction(() => {
        this._conversationAssignment = data
        this.syncLocalValuesFromServer(data)
      })
    } catch (error) {
      logger.error(error)

      if (rollbackCallback) {
        runInAction(() => {
          rollbackCallback()
        })
      }
      toastStore.add({
        title: 'Failed to update assignment settings',
        type: 'error',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  updateAssignmentType = async (type: AssignmentType) => {
    this._roundRobinMembersOrderChanged = false
    const previousAssignment = this._conversationAssignment
      ? {
          manual: { ...this._conversationAssignment.manual },
          round_robin: { ...this._conversationAssignment.round_robin },
          hubspot_assignment: { ...this._conversationAssignment.hubspot_assignment },
        }
      : null

    if (this._conversationAssignment) {
      this._conversationAssignment.manual.enabled = type === 'manual'
      this._conversationAssignment.round_robin.enabled = type === 'round_robin'
      this._conversationAssignment.hubspot_assignment.enabled = type === 'hubspot_assignment'

      if (type === 'round_robin') {
        this._selectedRoundRobinMembers = []
        this._roundRobinMembersOrderChanged = false
      }
    }

    if (
      type === 'hubspot_assignment' &&
      !this._conversationAssignment?.hubspot_assignment.integration_assignment_field
    ) {
      this._crmHubspotProperty = DEFAULT_HUBSPOT_PROPERTY
    }

    runInAction(() => {
      this._tabSwitchingLoading = true
    })

    const roundRobinUpdate =
      type === 'round_robin' && previousAssignment?.hubspot_assignment.enabled
        ? {
            enabled: true,
            users: this._members.map((member) => member.id),
          }
        : { enabled: type === 'round_robin' }

    await this.updateConversationAssignment(
      {
        manual: { enabled: type === 'manual' },
        round_robin: roundRobinUpdate,
        hubspot_assignment: {
          enabled: type === 'hubspot_assignment',
          integration_assignment_field: this._crmHubspotProperty,
        },
      },
      () => {
        if (previousAssignment && this._conversationAssignment) {
          this._conversationAssignment.manual = previousAssignment.manual
          this._conversationAssignment.round_robin = previousAssignment.round_robin
          this._conversationAssignment.hubspot_assignment = previousAssignment.hubspot_assignment
          this.syncLocalValuesFromServer(this._conversationAssignment)
        }
      }
    )

    runInAction(() => {
      this._tabSwitchingLoading = false
    })
  }

  updateSmartAssignment = async (enabled: boolean) => {
    const previousValue = this._smartAssignment
    this._smartAssignment = enabled
    await this.updateConversationAssignment(
      {
        manual: {
          smart_assignment_enabled: enabled,
        },
      },
      () => {
        this._smartAssignment = previousValue
      }
    )

    if (this._smartAssignment !== previousValue) {
      toastStore.add({
        title: enabled ? 'Smart assignment activated' : 'Smart assignment disabled',
        type: 'success',
      })
    }
  }

  loadMembers = async (inboxId: number) => {
    try {
      runInAction(() => {
        this._membersLoading = true
      })
      const { data } = await InboxesApi.getTeamByIdMembers(inboxId)
      runInAction(() => {
        this._members = data.filter((member) => member.user_role !== EnumUserRoles.VIEW_ONLY)

        if (
          this._conversationAssignment?.round_robin.enabled &&
          this._conversationAssignment.round_robin.users.length > 0 &&
          this._selectedRoundRobinMembers.length === 0 &&
          !this._roundRobinMembersOrderChanged
        ) {
          this._selectedRoundRobinMembers = this.createRoundRobinMembersFromServerOrder(
            this._conversationAssignment.round_robin.users,
            this._members
          )
        }

        if (
          this._conversationAssignment?.hubspot_assignment.enabled &&
          this._conversationAssignment.round_robin.enabled &&
          this._conversationAssignment.round_robin.users.length > 0 &&
          this._crmFallbackRoundRobinMembers.length === 0
        ) {
          this._crmFallbackRoundRobinMembers =
            this.createCRMFallbackRoundRobinMembersFromServerOrder(
              this._conversationAssignment.round_robin.users,
              this._members
            )
        }
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._membersLoading = false
      })
    }
  }

  setSearchValue = (value: string) => {
    this._searchValue = value
  }

  setSelectedMemberId = (id: number) => {
    this._selectedMemberId = id
  }

  private defaultMemberToDropdownItem = (member: IResponseUser) => {
    return {
      id: member.id,
      label: member.full_name || `${member.first_name} ${member.last_name}`,
      value: String(member.id),
      data: member,
      avatarProps: {
        info: {
          image: member.photo_url,
          firstName: member.first_name,
          lastName: member.last_name,
        },
        online: !member.isAway,
        size: 16,
      },
    }
  }

  private memberToDropdownItem = (member: IResponseUser) => {
    return {
      ...this.defaultMemberToDropdownItem(member),
      noRenderEmptyActiveValue: true,
      labelContent: <MemberLabel member={member} />,
      className: styles.selectOptionWrap,
    }
  }

  private memberToDropdownSmartAssignmentItem = (member: IResponseUser) => {
    return this.defaultMemberToDropdownItem(member)
  }

  updateManualAssignment = async (userId: number, options?: { keepHubspotEnabled?: boolean }) => {
    const previousValue = this._selectedMemberId
    this.setSelectedMemberId(userId)

    if (!this._conversationAssignment) {
      return
    }

    await this.updateConversationAssignment(
      {
        hubspot_assignment:
          options?.keepHubspotEnabled && this._conversationAssignment.hubspot_assignment.enabled
            ? { ...this._conversationAssignment.hubspot_assignment, enabled: true }
            : undefined,
        manual: {
          enabled: true,
          default_user_id: userId,
        },
        round_robin: {
          enabled: false,
          users: [],
        },
      },
      () => {
        this._selectedMemberId = previousValue
      }
    )
  }

  getSentimentValue = (field: SentimentField) => {
    switch (field) {
      case 'smart_assignment_positive':
        return this._sentimentPositive
      case 'smart_assignment_neutral':
        return this._sentimentNeutral
      case 'smart_assignment_negative':
        return this._sentimentNegative
      default:
        return null
    }
  }

  private setSentimentValue = (field: SentimentField, value: number | null) => {
    switch (field) {
      case 'smart_assignment_positive':
        this._sentimentPositive = value
        break
      case 'smart_assignment_neutral':
        this._sentimentNeutral = value
        break
      case 'smart_assignment_negative':
        this._sentimentNegative = value
        break
    }
  }

  updateSentimentAssignment = async (field: SentimentField, userId: number) => {
    const previousValue = this.getSentimentValue(field)
    this.setSentimentValue(field, userId)
    const valueToSave = userId === 0 ? null : userId

    await this.updateConversationAssignment(
      {
        manual: {
          [field]: valueToSave,
        },
      },
      () => {
        this.setSentimentValue(field, previousValue)
      }
    )
  }

  setSelectedRoundRobinMember = (value: IDropdownItem) => {
    this._selectedRoundRobinMembers = [...this._selectedRoundRobinMembers, value]
    this._roundRobinMembersOrderChanged = true
  }

  deleteSelectedRoundRobinMember = (value: IDropdownItem) => {
    this._selectedRoundRobinMembers = this._selectedRoundRobinMembers.filter(
      (item) => item.id !== value.id
    )
    this._roundRobinMembersOrderChanged = true
  }

  clearRoundRobinMembersError = () => {
    this._roundRobinMembersError = null
  }

  get hubspotFieldsAsDropdownItems(): IDropdownItem[] {
    return this._hubspotAssignmentFields.map((field) => ({
      id: field.name,
      label: field.label,
      value: field.name,
      data: field,
    }))
  }

  get selectedHubspotFieldItem() {
    return (
      this.hubspotFieldsAsDropdownItems.find((item) => item.value === this._crmHubspotProperty) ||
      null
    )
  }

  get crmFallbackType() {
    return this._crmFallbackType
  }

  get crmFallbackRoundRobinMembers() {
    return this._crmFallbackRoundRobinMembers
  }

  get filteredCRMFallbackMembers() {
    const unassignedItem: IDropdownItem = defaultUnassignedDropdownItem
    const roundRobinItem: IDropdownItem = defaultRoundRobinDropdownItem
    const memberItems = this._members.map(this.memberToDropdownItem)
    const allItems = [unassignedItem, roundRobinItem, ...memberItems]
    const searchLower = this._searchValue.toLowerCase().trim()

    return searchLower
      ? allItems.filter((item) => item.label.toLowerCase().includes(searchLower))
      : allItems
  }

  loadHubspotAssignmentFields = async () => {
    if (!this._inbox?.id) {
      return
    }
    try {
      const response = await InboxesApi.getHubspotAssignmentFields(this._inbox.id)

      this._hubspotAssignmentFields = response.data
    } catch (error) {
      logger.error(error)
    }
  }

  setSelectedCRMFallbackMember = (member: IDropdownItem) => {
    this._selectedCRMFallbackMember = member
  }

  setCRMFallbackType = async (type: 'unassigned' | 'round_robin' | null) => {
    this._crmFallbackType = type

    if (!this._conversationAssignment) {
      return
    }

    if (type === 'unassigned') {
      await this.updateConversationAssignment({
        hubspot_assignment: {
          ...this._conversationAssignment.hubspot_assignment,
          enabled: true,
        },
        manual: {
          enabled: true,
          default_user_id: 0,
        },
        round_robin: {
          enabled: false,
          users: [],
        },
      })
    } else if (type === 'round_robin') {
      this._crmFallbackRoundRobinSelected = true

      if (
        this._crmFallbackRoundRobinMembers.length === 0 &&
        this._conversationAssignment.round_robin.users.length > 0
      ) {
        const roundRobinUserIds = new Set(
          this.getRoundRobinUserIds(this._conversationAssignment.round_robin.users)
        )
        const membersToSelect = this._members.filter((member) => roundRobinUserIds.has(member.id))
        this._crmFallbackRoundRobinMembers = membersToSelect.map((member) =>
          this.allMembersAsDropdownItems(member)
        )
      }

      const userIds =
        this._crmFallbackRoundRobinMembers.length > 0
          ? this._crmFallbackRoundRobinMembers.map((item) => item.id as number)
          : this.getRoundRobinUserIds(this._conversationAssignment.round_robin.users)

      await this.updateConversationAssignment({
        hubspot_assignment: {
          ...this._conversationAssignment.hubspot_assignment,
          enabled: true,
        },
        manual: {
          enabled: false,
        },
        round_robin: {
          enabled: true,
          users: userIds,
        },
      })
    }
  }

  setCRMHubspotProperty = async (property: string | null) => {
    this._crmHubspotProperty = property
    await this.updateConversationAssignment({
      hubspot_assignment: {
        enabled: true,
        integration_assignment_field: property,
      },
    })
  }

  setCRMFallbackRoundRobinMember = (member: IDropdownItem) => {
    this._crmFallbackRoundRobinMembers = [...this._crmFallbackRoundRobinMembers, member]
  }

  deleteCRMFallbackRoundRobinMember = (member: IDropdownItem) => {
    this._crmFallbackRoundRobinMembers = this._crmFallbackRoundRobinMembers.filter(
      (item) => item.id !== member.id
    )
  }

  checkRoundRobinUsersChanges = (members: IDropdownItem[]) => {
    const currentUserIds = members.map((item) => item.id as number)
    const initialUserIds = this._conversationAssignment
      ? this.getRoundRobinUserIds(this._conversationAssignment.round_robin.users)
      : undefined

    return {
      hasChanges:
        currentUserIds.length !== initialUserIds?.length ||
        !currentUserIds.every((id) => initialUserIds?.includes(id)),
      currentUserIds,
      initialUserIds,
    }
  }

  updateCRMFallbackRoundRobinUsers = async () => {
    if (!this._conversationAssignment) {
      return
    }
    const { hasChanges, currentUserIds } = this.checkRoundRobinUsersChanges(
      this._crmFallbackRoundRobinMembers
    )

    if (!hasChanges) {
      return
    }

    this._crmFallbackRoundRobinSelected = true

    await this.updateConversationAssignment({
      hubspot_assignment: {
        ...this._conversationAssignment.hubspot_assignment,
        enabled: true,
      },
      round_robin: {
        enabled: true,
        users: currentUserIds,
      },
    })
  }

  updateRoundRobinUsers = async () => {
    if (!this._conversationAssignment) {
      return
    }
    if (this._selectedRoundRobinMembers.length === 0) {
      this._roundRobinMembersError = 'At least one member must be selected'
      return
    }
    const { hasChanges, currentUserIds } = this.checkRoundRobinUsersChanges(
      this._selectedRoundRobinMembers
    )

    if (!hasChanges) {
      return
    }

    await this.updateConversationAssignment({
      round_robin: {
        users: currentUserIds,
      },
    })
  }
}
